import * as React from 'react'
import { Theme } from '@mui/material/styles'
import { ChangeEvent, FC, useCallback } from 'react'
import { ErrorMessage, Field, Form, useFormikContext } from 'formik'

import { EditLeadNameFormModel } from './EditLeadNameFormWrapper'

import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ErrorMessageComponent } from '../ErrorMessageComponent.styled'

interface Props {
  onCancel: () => void
}

const styles = {
  error: {
    fontFamily: 'Source Sans Pro',
    fontSize: '12px',
    color: (theme: Theme) => theme.palette.error.main,
  },

  root: {
    width: '450px',
  },

  input: {
    display: 'flex',
  },

  submitBtn: {
    marginLeft: (theme: Theme) => theme.spacing(1),
  },
}

export const EditLeadNameFormFields: FC<Props> = (props: Props) => {
  const { onCancel } = props

  const {
    dirty,
    isValid,
    touched,
    errors,
    isSubmitting,
    handleBlur,
    handleChange,
    values: { company, firstName, lastName },
  } = useFormikContext<EditLeadNameFormModel>()

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (typeof event.target.value === 'string')
        event.target.value = event.target.value.trimStart()
      handleChange(event)
    },
    [handleChange],
  )
  const [requireCompany, requireName] = React.useMemo(
    () => [firstName === '' && lastName === '', company === ''],
    [firstName, lastName, company],
  )

  return (
    <Form autoComplete="off">
      <Box sx={styles.root}>
        <Typography>
          <b>Rename this Lead</b>
        </Typography>

        <Box mt={2}>
          <Field
            name="company"
            as={TextField}
            disabled={isSubmitting}
            error={touched.company && !!errors.company}
            fullWidth
            label="Company"
            margin="dense"
            onBlur={handleBlur}
            onChange={onChange}
            required={requireCompany}
            type="text"
            variant="outlined"
            data-test-id="companyto"
            inputProps={{ maxLength: 35 }}
          ></Field>
          <ErrorMessage component={ErrorMessageComponent} name="company" />
        </Box>

        <Box mt={1}>
          <Field
            name="firstName"
            as={TextField}
            disabled={isSubmitting}
            error={touched.firstName && !!errors.firstName}
            fullWidth
            label="First Name"
            margin="dense"
            onBlur={handleBlur}
            onChange={onChange}
            required={requireName}
            type="text"
            variant="outlined"
            data-test-id="firstName"
            inputProps={{ maxLength: 15 }}
          ></Field>
          <ErrorMessage component={ErrorMessageComponent} name="firstName" />
        </Box>

        <Box mt={1}>
          <Field
            name="lastName"
            as={TextField}
            disabled={isSubmitting}
            error={touched.lastName && !!errors.lastName}
            fullWidth
            label="Last Name"
            margin="dense"
            onBlur={handleBlur}
            onChange={onChange}
            required={requireName}
            type="text"
            variant="outlined"
            data-test-id="lastName"
            inputProps={{ maxLength: 20 }}
          ></Field>
          <ErrorMessage component={ErrorMessageComponent} name="lastName" />
        </Box>

        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button
            data-test-id="cancel-btn"
            onClick={onCancel}
            variant="outlined"
          >
            Cancel
          </Button>

          <Button
            sx={styles.submitBtn}
            color="primary"
            disabled={!dirty || isSubmitting || !isValid}
            type="submit"
            variant="contained"
            data-test-id="editLeadNameBtn"
          >
            Save
          </Button>
        </Box>
      </Box>
    </Form>
  )
}

EditLeadNameFormFields.displayName = 'EditLeadNameFormFields'
